/* Impulsa3 — Recetas: buscador (section `impulsa3-recipe-finder`).

   Todas las recetas se pintan en el HTML; aquí se filtran, ordenan y paginan en
   cliente. Eso es lo que arregla el fallo de la versión anterior, que filtraba
   el DOM de una parrilla ya paginada en servidor y por tanto solo buscaba
   dentro de la página visible.

   El estado vive también en la URL (?categoria=&q=&orden=&pagina=) para que un
   listado filtrado se pueda compartir o recargar sin perderlo. */

const SORT_KEYS = ['newest', 'oldest', 'az', 'za'];

/* Búsqueda insensible a acentos: «acai» encuentra «Açaí». */
const normalize = (value) =>
  (value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

class Impulsa3RecipeFinder extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('[data-impulsa3-grid]');
    if (!this.grid) return;

    this.searchInput = this.querySelector('[data-impulsa3-search]');
    this.submitButton = this.querySelector('[data-impulsa3-submit]');
    this.statusElement = this.querySelector('[data-impulsa3-status]');
    this.emptyElement = this.querySelector('[data-impulsa3-empty]');
    this.paginationElement = this.querySelector('[data-impulsa3-pagination]');
    this.sortCurrentElement = this.querySelector('[data-impulsa3-sort-current]');
    this.categoryButtons = Array.from(this.querySelectorAll('[data-impulsa3-category]'));
    this.sortButtons = Array.from(this.querySelectorAll('[data-impulsa3-sort]'));
    this.dropdowns = Array.from(this.querySelectorAll('[data-impulsa3-dropdown]'));

    this.perPage = Number.parseInt(this.dataset.perPage, 10) || 9;
    this.appliedSort = null;

    this.recipes = Array.from(this.querySelectorAll('[data-impulsa3-card]')).map((element) => ({
      element,
      order: Number.parseInt(element.dataset.order, 10) || 0,
      name: element.dataset.name || '',
      category: element.dataset.category || '',
      categoryKey: normalize(element.dataset.category),
      searchKey: normalize(element.dataset.search),
    }));

    this.state = this.#stateFromUrl();

    this.#bindEvents();
    this.#render({ updateUrl: false });
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.handleDocumentClick);
  }

  /* ── Estado ───────────────────────────────────────────────────────────── */

  #stateFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const defaultSort = SORT_KEYS.includes(this.dataset.defaultSort) ? this.dataset.defaultSort : 'az';
    const requestedSort = params.get('orden');
    const requestedCategory = normalize(params.get('categoria'));

    /* Una categoría que ya no existe (receta despublicada, cambio de nombre)
       no debe dejar el listado vacío sin explicación: se ignora. */
    const category = this.recipes.some((recipe) => recipe.categoryKey === requestedCategory)
      ? requestedCategory
      : '';

    return {
      category,
      query: params.get('q') || '',
      sort: SORT_KEYS.includes(requestedSort) ? requestedSort : defaultSort,
      page: Math.max(1, Number.parseInt(params.get('pagina'), 10) || 1),
    };
  }

  #updateUrl() {
    const params = new URLSearchParams(window.location.search);
    const values = {
      categoria: this.state.category,
      q: this.state.query.trim(),
      orden: this.state.sort === this.dataset.defaultSort ? '' : this.state.sort,
      pagina: this.state.page > 1 ? String(this.state.page) : '',
    };

    Object.entries(values).forEach(([key, value]) => {
      if (value) {
        params.set(key, value);
      } else {
        params.delete(key);
      }
    });

    const search = params.toString();
    window.history.replaceState({}, '', `${window.location.pathname}${search ? `?${search}` : ''}${window.location.hash}`);
  }

  /* ── Eventos ──────────────────────────────────────────────────────────── */

  #bindEvents() {
    this.searchInput?.addEventListener('input', () => {
      window.clearTimeout(this.searchTimeout);
      this.searchTimeout = window.setTimeout(() => {
        this.state.query = this.searchInput.value;
        this.state.page = 1;
        this.#render();
      }, 150);
    });

    this.searchInput?.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      this.searchInput.blur();
    });

    this.submitButton?.addEventListener('click', () => {
      if (!this.searchInput) return;

      if (this.searchInput.value.trim() === '') {
        this.searchInput.focus();
      } else {
        this.#scrollToResults();
      }
    });

    this.categoryButtons.forEach((button) => {
      button.addEventListener('click', () => {
        this.state.category = normalize(button.dataset.impulsa3Category);
        this.state.page = 1;
        this.#closeDropdowns();
        this.#render();
      });
    });

    this.sortButtons.forEach((button) => {
      button.addEventListener('click', () => {
        this.state.sort = button.dataset.impulsa3Sort;
        this.state.page = 1;
        this.#closeDropdowns();
        this.#render();
      });
    });

    this.dropdowns.forEach((dropdown) => {
      const toggle = dropdown.querySelector('[data-impulsa3-dropdown-toggle]');

      toggle?.addEventListener('click', (event) => {
        event.stopPropagation();
        const isOpen = dropdown.classList.contains('is-open');
        this.#closeDropdowns();
        if (!isOpen) this.#openDropdown(dropdown);
      });

      dropdown.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        this.#closeDropdowns();
        toggle?.focus();
      });
    });

    document.addEventListener('click', this.handleDocumentClick);
  }

  handleDocumentClick = (event) => {
    // Solo se mantiene abierto si el clic ha caído dentro del propio
    // desplegable; cualquier otro sitio (incluido el campo de búsqueda) cierra.
    if (this.dropdowns.some((dropdown) => dropdown.contains(event.target))) return;
    this.#closeDropdowns();
  };

  #openDropdown(dropdown) {
    dropdown.classList.add('is-open');
    dropdown.querySelector('[data-impulsa3-dropdown-toggle]')?.setAttribute('aria-expanded', 'true');
    dropdown.querySelector('[data-impulsa3-dropdown-menu]')?.removeAttribute('hidden');
  }

  #closeDropdowns() {
    this.dropdowns.forEach((dropdown) => {
      dropdown.classList.remove('is-open');
      dropdown.querySelector('[data-impulsa3-dropdown-toggle]')?.setAttribute('aria-expanded', 'false');
      dropdown.querySelector('[data-impulsa3-dropdown-menu]')?.setAttribute('hidden', '');
    });
  }

  #scrollToResults() {
    this.grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /* ── Pintado ──────────────────────────────────────────────────────────── */

  #matches(recipe, { category, queryKey }) {
    if (category && recipe.categoryKey !== category) return false;
    if (queryKey && !recipe.searchKey.includes(queryKey)) return false;
    return true;
  }

  #render({ updateUrl = true } = {}) {
    const queryKey = normalize(this.state.query);
    const criteria = { category: this.state.category, queryKey };

    if (this.appliedSort !== this.state.sort) {
      this.#applySort();
      this.appliedSort = this.state.sort;
    }

    const visible = this.recipes.filter((recipe) => this.#matches(recipe, criteria));
    const totalPages = Math.max(1, Math.ceil(visible.length / this.perPage));
    this.state.page = Math.min(this.state.page, totalPages);

    const start = (this.state.page - 1) * this.perPage;
    const end = start + this.perPage;

    this.recipes.forEach((recipe) => {
      recipe.element.hidden = true;
    });

    visible.slice(start, end).forEach((recipe) => {
      recipe.element.hidden = false;
    });

    if (this.emptyElement) this.emptyElement.hidden = visible.length > 0;
    if (this.statusElement) {
      this.statusElement.textContent = `${visible.length} ${visible.length === 1 ? 'receta' : 'recetas'}`;
    }

    this.#renderCategoryState(queryKey);
    this.#renderSortState();
    this.#renderPagination(totalPages);

    if (updateUrl) this.#updateUrl();
  }

  #applySort() {
    const sorted = [...this.recipes];

    sorted.sort((a, b) => {
      switch (this.state.sort) {
        /* Liquid no da la fecha del metaobjeto: el id crece con cada alta, así
           que sirve de orden de creación. */
        case 'newest':
          return b.order - a.order;
        case 'oldest':
          return a.order - b.order;
        case 'za':
          return b.name.localeCompare(a.name, 'es', { sensitivity: 'base' });
        default:
          return a.name.localeCompare(b.name, 'es', { sensitivity: 'base' });
      }
    });

    const fragment = document.createDocumentFragment();
    sorted.forEach((recipe) => fragment.appendChild(recipe.element));
    this.grid.appendChild(fragment);

    this.recipes = sorted;
  }

  /* Los contadores cuentan sobre la búsqueda actual, no sobre el catálogo
     entero: así se ve de antemano qué categoría tiene resultados. */
  #renderCategoryState(queryKey) {
    this.categoryButtons.forEach((button) => {
      const category = normalize(button.dataset.impulsa3Category);
      const matches = this.recipes.filter((recipe) => this.#matches(recipe, { category, queryKey }));
      const counter = button.querySelector('[data-impulsa3-count]');

      if (counter) counter.textContent = matches.length > 0 ? String(matches.length) : '';
      button.setAttribute('aria-pressed', category === this.state.category ? 'true' : 'false');
      button.disabled = matches.length === 0 && category !== this.state.category;
    });
  }

  #renderSortState() {
    this.sortButtons.forEach((button) => {
      const isCurrent = button.dataset.impulsa3Sort === this.state.sort;
      button.setAttribute('aria-pressed', isCurrent ? 'true' : 'false');
      if (isCurrent && this.sortCurrentElement) {
        this.sortCurrentElement.textContent = button.textContent.trim();
      }
    });
  }

  #renderPagination(totalPages) {
    if (!this.paginationElement) return;

    this.paginationElement.textContent = '';
    this.paginationElement.hidden = totalPages <= 1;
    if (totalPages <= 1) return;

    this.paginationElement.appendChild(this.#paginationArrow('‹', this.state.page - 1, this.state.page === 1));

    this.#pageNumbers(totalPages).forEach((page) => {
      if (page === null) {
        const gap = document.createElement('span');
        gap.className = 'impulsa3-recipe-finder__page';
        gap.textContent = '…';
        this.paginationElement.appendChild(gap);
        return;
      }

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'impulsa3-recipe-finder__page';
      button.textContent = String(page);

      if (page === this.state.page) {
        button.setAttribute('aria-current', 'page');
      } else {
        button.addEventListener('click', () => this.#goToPage(page));
      }

      this.paginationElement.appendChild(button);
    });

    this.paginationElement.appendChild(
      this.#paginationArrow('›', this.state.page + 1, this.state.page === totalPages)
    );
  }

  #paginationArrow(label, page, disabled) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'impulsa3-recipe-finder__arrow';
    button.textContent = label;
    button.disabled = disabled;
    button.setAttribute('aria-label', label === '‹' ? 'Página anterior' : 'Página siguiente');
    if (!disabled) button.addEventListener('click', () => this.#goToPage(page));
    return button;
  }

  /* Con muchas páginas se resume: 1 … 4 5 6 … 12 */
  #pageNumbers(totalPages) {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    const pages = new Set([1, totalPages, this.state.page]);
    if (this.state.page - 1 > 1) pages.add(this.state.page - 1);
    if (this.state.page + 1 < totalPages) pages.add(this.state.page + 1);

    const ordered = [...pages].sort((a, b) => a - b);
    const withGaps = [];

    ordered.forEach((page, index) => {
      if (index > 0 && page - ordered[index - 1] > 1) withGaps.push(null);
      withGaps.push(page);
    });

    return withGaps;
  }

  #goToPage(page) {
    this.state.page = page;
    this.#render();
    this.#scrollToResults();
  }
}

if (!customElements.get('impulsa3-recipe-finder')) {
  customElements.define('impulsa3-recipe-finder', Impulsa3RecipeFinder);
}
